import { FormGroup, Input, Label } from 'reactstrap';
import { TableEditorProps, TableData } from '../../types';
import React, { useState } from 'react';
import {
  MAX_LENGTH_TABLE_TITLE,
  getCalculationValues,
  getDirectTableRowData,
  getTableData,
  getUniqueUtrVariables,
  isValidTableData,
} from '../../utils/table-utils';
import { CalculationType, TableSubType, TableWidgetColumnType } from '@g17eco/types/insight-custom-dashboard';
import { MultiRowTableInput } from './MultiRowTableInput';
import { transformVariables } from '@routes/custom-dashboard/utils';
import { EditorTemplate } from '../EditorTemplate';
import { useGetInitiativeBlueprintQuestionsQuery } from '@api/admin-dashboard';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';

type HiddenColumnType = TableWidgetColumnType.Unit | TableWidgetColumnType.Sparkline;

export const TableEditor = (props: TableEditorProps) => {
  const isUserStaff = useAppSelector(isStaff);

  const { editingItem, handleCancel, updateItem, initiativeId } = props;
  const { data: blueprintQuestions = [] } = useGetInitiativeBlueprintQuestionsQuery({ initiativeId });
  const [tableData, setTableData] = useState<TableData>(getTableData(editingItem));
  const [title, setTitle] = useState<string>('title' in editingItem && editingItem.title ? editingItem.title : '');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(
    'config' in editingItem && editingItem.config?.hiddenColumns
      ? editingItem.config.hiddenColumns
      : [TableWidgetColumnType.Sparkline],
  );
  const [wrapText, setWrapText] = useState<boolean>(('config' in editingItem && editingItem.config?.wrapText) ?? false);

  const invalidData = !isValidTableData(tableData.rowData, blueprintQuestions);
  const hasDirectTable = getDirectTableRowData(tableData.rowData, blueprintQuestions).length > 0;
  const hiddenCols = hasDirectTable ? [TableWidgetColumnType.Unit, TableWidgetColumnType.Sparkline] : hiddenColumns;

  const getChecked = (key: HiddenColumnType) => !hiddenCols.includes(key);

  const toggleDisplay = (key: HiddenColumnType) => {
    if (hiddenColumns.includes(key)) {
      setHiddenColumns(hiddenColumns.filter((column) => column !== key));
    } else {
      setHiddenColumns([...hiddenColumns, key]);
    }
  };

  const handleSubmit = async () => {
    const variables = transformVariables(getUniqueUtrVariables(tableData.rowData));
    const calculation = {
      type: CalculationType.Formula,
      values: getCalculationValues({ rowData: tableData.rowData, variables }),
    };
    const config = {
      hiddenColumns: hiddenCols,
      wrapText,
    };

    const subType = hasDirectTable ? TableSubType.DirectTable : TableSubType.VariableTable;
    return updateItem({ ...editingItem, subType, title, variables, calculation, config });
  };

  return (
    <EditorTemplate isDisabled={invalidData} handleCancel={handleCancel} handleSubmit={handleSubmit}>
      <p className='text-medium mt-4 mb-1'>Table title</p>
      <Input
        type='text'
        className='text-md'
        maxLength={MAX_LENGTH_TABLE_TITLE}
        value={title ?? ''}
        onChange={(e) => setTitle(e.target.value)}
        placeholder='Table title (optional)'
      />
      <div className='mt-4'>
        <Label className='text-md text-ThemeTextDark' check>
          Display
        </Label>
        <div className='d-flex align-items-center mt-2 gap-4'>
          <FormGroup switch className='d-flex align-items-center text-muted gap-2'>
            <Input
              type='switch'
              role='switch'
              checked={getChecked(TableWidgetColumnType.Unit)}
              onChange={() => toggleDisplay(TableWidgetColumnType.Unit)}
              disabled={hasDirectTable}
            />
            <Label className='text-md text-ThemeTextDark' check>
              Units
            </Label>
          </FormGroup>
          <FormGroup switch className='d-flex align-items-center text-muted gap-2'>
            <Input
              type='switch'
              role='switch'
              checked={getChecked(TableWidgetColumnType.Sparkline)}
              onChange={() => toggleDisplay(TableWidgetColumnType.Sparkline)}
              disabled={hasDirectTable}
            />
            <Label className='text-md text-ThemeTextDark' check>
              Sparkline
            </Label>
          </FormGroup>
        </div>
      </div>
      <MultiRowTableInput initiativeId={initiativeId} tableData={tableData} setTableData={setTableData} />
      {hasDirectTable && isUserStaff ? (
        <FormGroup check className='my-3'>
          <Label check>
            <Input
              type='checkbox'
              checked={wrapText}
              onChange={(e) => {
                setWrapText(e.target.checked);
              }}
            />
            Wrap text for exports (rather than clip)
          </Label>
        </FormGroup>
      ) : null}
    </EditorTemplate>
  );
};
